export const SIZE = 5;
export const ROWS = ['A', 'B', 'C', 'D', 'E'];
export const COORDINATES = ROWS.flatMap(row => Array.from({ length: SIZE }, (_, col) => `${row}${col + 1}`));
export const PAPER_URL = 'https://nhsjs.com/2026/empirical-evaluation-of-stochastic-rule-layers-in-a-partially-observable-battleship-game/';
export const LEVELS = [
  { id: 'L0', name: 'Classical baseline', short: 'Classic', description: 'Ships stay in one fixed position. An attack on an undamaged ship square is a hit.' },
  { id: 'L1', name: 'Attack-type vulnerability', short: 'Vulnerability', description: 'L0 + weapon-dependent defense. A ship square may resist a bomb or torpedo, so even a shot on it can miss.' },
  { id: 'L2', name: 'Branch-based placement', short: 'Superposition', description: 'L1 + optional two-position ships. A position measurement keeps one branch and discards the other.' },
  { id: 'L3', name: 'Cross-grid correlated damage', short: 'Linked damage', description: 'L2 + links between the two fleets. Destroying a linked square also destroys its partner; both fleets can lose together.' },
];
export const SHIPS = [{ id: 's1', name: 'S1', length: 2 }, { id: 's2', name: 'S2', length: 3 }, { id: 's3', name: 'S3', length: 4 }];
export const DEFAULT_AI_STRATEGY = 'checkerboard';
export const AI_STRATEGIES = [
  { id: 'random', name: 'Random targeting', description: 'Chooses available squares without following hit patterns.' },
  { id: 'checkerboard', name: 'Checkerboard with follow-up', description: 'Searches alternating squares, then follows nearby hits to finish a ship.' },
  { id: 'belief', name: 'Belief-state probability mapping', description: 'Updates possible fleet probabilities after each observation and chooses the most likely target.' },
  { id: 'greedy', name: 'Greedy maximum-probability targeting', description: 'Chooses the square covered by the most compatible ship placements.' },
];
export const aiStrategyInfo = strategy => AI_STRATEGIES.find(item => item.id === strategy);
export function normalizeSettings(value = {}) {
  return { level: LEVELS.some(item => item.id === value?.level) ? value.level : 'L2',
    executionMode: value?.executionMode === 'hardware' ? 'hardware' : 'simulation',
    aiStrategy: aiStrategyInfo(value?.aiStrategy) ? value.aiStrategy : DEFAULT_AI_STRATEGY };
}
export const levelInfo = level => LEVELS.find(item => item.id === level) || LEVELS[2];
export const hasBranches = level => level === 'L2' || level === 'L3';
export const hasLinks = level => level === 'L3';
export const position = coordinate => [ROWS.indexOf(coordinate?.[0]), Number(coordinate?.slice(1)) - 1];
export const isCoordinate = coordinate => COORDINATES.includes(coordinate);
export const createFleet = level => SHIPS.map(ship => ({ ...ship, branchCount: hasBranches(level) ? 2 : 1, placements: [] }));
export function footprint(start, length, orientation = 'horizontal') {
  if (!isCoordinate(start)) return [];
  const [row, col] = position(start);
  if ((orientation === 'horizontal' ? col : row) + length > SIZE) return [];
  return Array.from({ length }, (_, i) => `${ROWS[row + (orientation === 'vertical' ? i : 0)]}${col + (orientation === 'horizontal' ? i : 0) + 1}`);
}
export function placementError(cells, fleet, length) {
  if (cells.length !== length || cells.some(cell => !isCoordinate(cell))) return 'That ship extends beyond the board. Choose a different starting square.';
  const used = new Set(fleet.flatMap(ship => ship.placements.flatMap(branch => branch.cells)));
  return cells.some(cell => used.has(cell)) ? 'That position overlaps another position. Choose a clear run of squares.' : '';
}
export function validateFleet(fleet, level) {
  if (!Array.isArray(fleet) || fleet.length !== SHIPS.length) return 'The fleet needs S1, S2, and S3.';
  const used = new Set();
  for (const definition of SHIPS) {
    const matching = fleet.filter(ship => ship.id === definition.id);
    if (matching.length !== 1 || matching[0].length !== definition.length) return 'The fleet needs S1 (2 squares), S2 (3), and S3 (4).';
    const ship = matching[0];
    if (![1, 2].includes(ship.branchCount) || (!hasBranches(level) && ship.branchCount !== 1)) return `${ship.name} has an invalid number of positions.`;
    if (!Array.isArray(ship.placements) || ship.placements.length !== ship.branchCount) return `Finish placing ${ship.name}.`;
    for (const branch of ship.placements) {
      const cells = branch?.cells;
      if (!Array.isArray(cells) || cells.length !== ship.length || new Set(cells).size !== ship.length || cells.some(cell => !isCoordinate(cell))) return `${ship.name} has an invalid position.`;
      const locations = cells.map(position);
      const horizontal = locations.every(([row]) => row === locations[0][0]);
      const vertical = locations.every(([, col]) => col === locations[0][1]);
      const indices = locations.map(item => item[horizontal ? 1 : 0]).sort((a, b) => a - b);
      if ((!horizontal && !vertical) || indices.some((value, i) => value !== indices[0] + i)) return `${ship.name} must occupy a straight, continuous run of squares.`;
      if (cells.some(cell => used.has(cell))) return 'Ship positions must not overlap.';
      cells.forEach(cell => used.add(cell));
    }
  }
  return '';
}
export const fleetPayload = fleet => fleet.map(ship => ({ shipId: ship.id, name: ship.name, length: ship.length, superposed: ship.branchCount === 2,
  placements: ship.placements.map(branch => ({ cells: branch.cells, probability: 1 / ship.branchCount })) }));
// Backtracking keeps a six-position fleet legal without a guessing limit.
export function shuffleFleet(fleet, random = Math.random) {
  const tasks = fleet.flatMap(ship => Array.from({ length: ship.branchCount }, (_, branch) => ({ shipId: ship.id, length: ship.length, branch }))).sort((a, b) => b.length - a.length);
  const candidates = new Map(SHIPS.map(ship => [ship.length, COORDINATES.flatMap(start => ['horizontal', 'vertical'].map(direction => footprint(start, ship.length, direction))).filter(cells => cells.length)]));
  const placed = new Map();
  function search(index, occupied) {
    if (index === tasks.length) return true;
    const task = tasks[index];
    const choices = [...candidates.get(task.length)];
    for (let i = choices.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [choices[i], choices[j]] = [choices[j], choices[i]];
    }
    for (const cells of choices) {
      if (cells.some(cell => occupied.has(cell))) continue;
      placed.set(`${task.shipId}:${task.branch}`, cells);
      if (search(index + 1, new Set([...occupied, ...cells]))) return true;
    }
    return false;
  }
  if (!search(0, new Set())) throw new Error('This fleet cannot fit on the board.');
  return fleet.map(ship => ({ ...ship, placements: Array.from({ length: ship.branchCount }, (_, branch) => ({ cells: placed.get(`${ship.id}:${branch}`) })) }));
}
export function shipPositions(ships = []) {
  return ships.flatMap(ship => {
    const unresolved = ship.superposed && !ship.collapsed;
    const branches = unresolved ? ship.branches : [{ cells: (ship.segments || []).map(segment => segment.coordinate), probability: 1 }];
    return (branches || []).map((branch, index) => ({ id: `${ship.id}:${index}`, shipId: ship.id, name: ship.name,
      cells: branch.cells, probability: unresolved ? branch.probability : 1, possible: unresolved }));
  });
}
export function occupancyMap(positions) {
  const map = Object.fromEntries(COORDINATES.map(cell => [cell, 0]));
  for (const branch of positions) for (const cell of branch.cells || []) if (isCoordinate(cell)) map[cell] = Math.min(1, map[cell] + branch.probability);
  return map;
}
export function marksFor(history = [], attacker, hits = []) {
  const marks = {};
  for (const attack of history) if (attack.attacker === attacker && isCoordinate(attack.coordinate)) {
    if (!['hit', 'sunk'].includes(marks[attack.coordinate]?.result)) marks[attack.coordinate] = attack;
  }
  for (const cell of hits) if (isCoordinate(cell) && !['hit', 'sunk'].includes(marks[cell]?.result)) marks[cell] = { coordinate: cell, result: 'hit', linked: true };
  return marks;
}
export function groupTurns(history = []) {
  const turns = new Map();
  for (const attack of history) {
    if (!turns.has(attack.turn_number)) turns.set(attack.turn_number, { number: attack.turn_number });
    turns.get(attack.turn_number)[attack.attacker] = attack;
  }
  return [...turns.values()].sort((a, b) => a.number - b.number);
}
export function statistics(history = [], attacker = 'player') {
  const attacks = history.filter(attack => attack.attacker === attacker);
  const hits = attacks.filter(attack => attack.result === 'hit' || attack.result === 'sunk').length;
  return { shots: attacks.length, hits, accuracy: attacks.length ? Math.round(1000 * hits / attacks.length) / 10 : 0,
    sinks: attacks.filter(attack => attack.result === 'sunk').length,
    collapses: attacks.filter(attack => attack.collapse_occurred).length,
    linked: attacks.reduce((count, attack) => count + (attack.entangled_coordinates_destroyed?.length || 0), 0) };
}
export const resultLabel = result => ({ hit: 'Hit', miss: 'Miss', sunk: 'Ship destroyed' }[result] || 'No shot');
export const attackText = attack => !attack ? 'No shot' : `${attack.coordinate} · ${attack.attack_type === 'torpedo' ? 'Torpedo' : 'Bomb'} · ${resultLabel(attack.result)}`;
export const winnerText = winner => ({ player: 'You won.', ai: 'The opponent won.', draw: 'Draw.' }[winner] || 'Match complete.');
export function shotsCsv(game) {
  const fields = ['game_id', 'level', 'ai_strategy', 'turn_number', 'attacker', 'coordinate', 'attack_type', 'result', 'damage_applied', 'collapse_occurred', 'immunity_revealed', 'basis_changed', 'r1_value', 'r2_value', 'entanglement_triggered', 'entangled_coordinates_destroyed', 'timestamp'];
  const escape = value => `"${String(Array.isArray(value) ? value.join(';') : value ?? '').replaceAll('"', '""')}"`;
  const metadata = { game_id: game.game_id, level: game.level, ai_strategy: game.ai_strategy };
  return [fields.join(','), ...(game.attack_history || []).map(attack => {
    const row = { ...attack, ...metadata };
    return fields.map(field => escape(row[field])).join(',');
  })].join('\r\n');
}
